import { useCallback, useState } from 'react';
import { useNavigation } from 'utils/context/NavigationContext';

interface UseStateWithOptions<T> {
  id: string;
  initialState: T;
  onBack?: (
    state: T,
    options?: {
      decrement?: number;
      customBoolean?: boolean;
      customString?: string;
    },
  ) => T;
  decrement?: number;
  customBoolean?: boolean;
  customString?: string;
}

function useGeneralState<T>({
  id,
  initialState,
  onBack = defaultBackAction,
  decrement = 1,
  customBoolean = false,
  customString = '',
}: UseStateWithOptions<T>) {
  const {
    pushToStack,
    popFromStack,
    updateStack,
    deleteFromStack,
    removeFromStack,
    navigationStack,
  } = useNavigation();
  const [state, setStateInternal] = useState<T>(initialState);

  // Enhanced setState that manages the navigation stack
  const setState = useCallback(
    (
      newState: T,
      operation: 'Update' | 'Push' | 'Pop' | 'Delete' | 'Remove' = 'Update',
      customOnBack?: (currentState: T) => T,
    ) => {
      const existingIndex = navigationStack.findIndex(item => item.id === id);
      const shouldUpdate = existingIndex !== -1 && operation === 'Update';
      if (shouldUpdate) {
        updateStack(id, {
          ...navigationStack[existingIndex],
          onBack: () => {
            const updatedState = customOnBack
              ? customOnBack(state)
              : onBack(state, {
                  decrement,
                  customBoolean,
                  customString,
                });
            setStateInternal(updatedState);
          },
        });
      } else if (
        operation === 'Pop' &&
        navigationStack.length &&
        id === navigationStack.slice(-1)[0].id
      ) {
        popFromStack();
      } else if (operation === 'Delete') {
        deleteFromStack(id);
      } else if (operation === 'Remove') {
        // Remove from stack without triggering onBack callback (for programmatic closes)
        removeFromStack(id);
      } else {
        // Push new item to stack, handle back action differently based on forcePush
        pushToStack({
          id,
          onBack: () => {
            if (customOnBack) {
              const revertedState = customOnBack(state);
              setStateInternal(revertedState);
            } else if (existingIndex !== -1) {
              setStateInternal(state); // revert to the previous state without custom logic
            } else {
              // Normal back action
              const updatedState = onBack(state, {
                decrement,
                customBoolean,
                customString,
              });
              setStateInternal(updatedState);
            }
          },
        });
      }
      setStateInternal(newState);
    },
    [
      navigationStack,
      id,
      updateStack,
      state,
      onBack,
      decrement,
      customBoolean,
      customString,
      popFromStack,
      deleteFromStack,
      removeFromStack,
      pushToStack,
    ],
  );

  const close = useCallback(() => {
    popFromStack();
  }, [popFromStack]);

  return { state, setState, close };
}

function defaultBackAction<T>(
  state: T,
  options: {
    decrement?: number;
    customBoolean?: boolean;
    customString?: string;
  } = {},
): T {
  const { decrement = 1, customBoolean = false, customString = '' } = options;

  if (typeof state === 'number') {
    return Math.max(0, state - decrement) as T;
  } else if (typeof state === 'boolean') {
    return customBoolean as unknown as T;
  } else if (typeof state === 'string') {
    return customString as unknown as T;
  }
  return state;
}

export default useGeneralState;
